import { useMemo, useRef, useState } from 'react';
import {
  AnchoredPanel, Button, Checkbox, ColorInput, IconButton, Input, Modal, ModalBody, ModalFooter, ModalHeader,
  OptionChip, RangeInput, Select, inputClass,
} from '../ui';
import {
  AdjustmentsHorizontalIcon,
  Bars3BottomLeftIcon,
  BellAlertIcon,
  CalendarDaysIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  HashtagIcon,
  InformationCircleIcon,
  ListBulletIcon,
  MapPinIcon,
  PencilIcon,
  PencilSquareIcon,
  PhotoIcon,
  QuestionMarkCircleIcon,
  Squares2X2Icon,
  TableCellsIcon,
  TrashIcon,
  DocumentIcon,
} from '@heroicons/react/24/outline';
import { CONDITION_OPS, newId } from '../../lib/agentShared/formSchema';
import type {
  Condition, FieldOption, FieldType, FormField, FormRelation, RelationType,
} from '../../lib/agentShared/formSchema';

// Primitives moved to the design system (0-4); re-exported so existing
// imports keep working. New code should import from components/ui.
export { AnchoredPanel, OptionChip, inputClass };

// Layman-friendly names for condition operators (shown in dropdowns and
// rendered logic rows — the schema keeps the compact codes).
export const OP_LABELS: Record<string, string> = {
  eq: 'is',
  neq: 'is not',
  in: 'is one of',
  gt: 'is greater than',
  gte: 'is at least',
  lt: 'is less than',
  lte: 'is at most',
  answered: 'is answered',
  notAnswered: 'is not answered',
};
export const opLabel = (op: string): string => OP_LABELS[op] ?? op;

// Human labels for the response-type picker (SafetyCulture-style naming).
export const TYPE_LABELS: Record<FieldType, string> = {
  text: 'Text answer',
  textarea: 'Paragraph',
  number: 'Number',
  boolean: 'Checkbox',
  select: 'Multiple choice',
  multiselect: 'Checkbox list',
  rating: 'Slider',
  date: 'Date',
  datetime: 'Date & Time',
  photo: 'Media',
  signature: 'Signature',
  gps: 'Location',
  instruction: 'Instruction',
};

const ICON_CLASS = 'h-4 w-4';
export const TYPE_ICONS: Record<FieldType, JSX.Element> = {
  text: <DocumentTextIcon className={`${ICON_CLASS} text-sky-500`} />,
  textarea: <Bars3BottomLeftIcon className={`${ICON_CLASS} text-sky-500`} />,
  number: <HashtagIcon className={`${ICON_CLASS} text-teal-500`} />,
  boolean: <CheckCircleIcon className={`${ICON_CLASS} text-emerald-500`} />,
  select: <ListBulletIcon className={`${ICON_CLASS} text-violet-500`} />,
  multiselect: <ListBulletIcon className={`${ICON_CLASS} text-violet-500`} />,
  rating: <AdjustmentsHorizontalIcon className={`${ICON_CLASS} text-orange-500`} />,
  date: <CalendarIcon className={`${ICON_CLASS} text-amber-500`} />,
  datetime: <CalendarDaysIcon className={`${ICON_CLASS} text-amber-500`} />,
  photo: <PhotoIcon className={`${ICON_CLASS} text-emerald-500`} />,
  signature: <PencilSquareIcon className={`${ICON_CLASS} text-indigo-500`} />,
  gps: <MapPinIcon className={`${ICON_CLASS} text-orange-500`} />,
  instruction: <InformationCircleIcon className={`${ICON_CLASS} text-sky-500`} />,
};

export const RESPONSE_SET_PRESETS: Array<{ label: string; options: FieldOption[] }> = [
  {
    label: 'Yes / No / N/A',
    options: [
      { label: 'Yes', color: '#22c55e', score: 1 },
      { label: 'No', color: '#ef4444', score: 0, failed: true },
      { label: 'N/A', color: '#94a3b8' },
    ],
  },
  {
    label: 'Good / Fair / Poor / N/A',
    options: [
      { label: 'Good', color: '#22c55e', score: 2 },
      { label: 'Fair', color: '#eab308', score: 1 },
      { label: 'Poor', color: '#ef4444', score: 0, failed: true },
      { label: 'N/A', color: '#94a3b8' },
    ],
  },
  {
    label: 'Safe / At Risk / N/A',
    options: [
      { label: 'Safe', color: '#22c55e', score: 1 },
      { label: 'At Risk', color: '#ef4444', score: 0, failed: true },
      { label: 'N/A', color: '#94a3b8' },
    ],
  },
  {
    label: 'Pass / Fail / N/A',
    options: [
      { label: 'Pass', color: '#22c55e', score: 1 },
      { label: 'Fail', color: '#ef4444', score: 0, failed: true },
      { label: 'N/A', color: '#94a3b8' },
    ],
  },
  {
    label: 'Compliant / Non-Compliant / N/A',
    options: [
      { label: 'Compliant', color: '#22c55e', score: 1 },
      { label: 'Non-Compliant', color: '#ef4444', score: 0, failed: true },
      { label: 'N/A', color: '#94a3b8' },
    ],
  },
  {
    label: 'Excellent / Good / Fair / Poor',
    options: [
      { label: 'Excellent', color: '#16a34a', score: 3 },
      { label: 'Good', color: '#22c55e', score: 2 },
      { label: 'Fair', color: '#eab308', score: 1 },
      { label: 'Poor', color: '#ef4444', score: 0, failed: true },
    ],
  },
];

// ------------------------------------------------------------------
// Response-set editor modal — SC's "Multiple choice responses" dialog:
// sortable rows (label, color, flagged, score), Scoring toggle,
// + Add Response, Save and apply.
// ------------------------------------------------------------------
export function ResponseSetModal({ initial, onSave, onClose }: {
  initial: FieldOption[];
  onSave: (options: FieldOption[]) => void;
  onClose: () => void;
}): JSX.Element {
  const [options, setOptions] = useState<FieldOption[]>(
    () => (initial.length > 0 ? initial : [{ label: 'Option 1' }, { label: 'Option 2' }])
      .map((o) => ({ ...o, id: o.id ?? newId() })),
  );
  const [scoring, setScoring] = useState(initial.some((o) => o.score != null));
  const dragIndex = useRef<number | null>(null);

  const patch = (id: string | undefined, p: Partial<FieldOption>) =>
    setOptions((prev) => prev.map((o) => (o.id === id ? { ...o, ...p } : o)));

  const clearScore = (id: string | undefined) =>
    setOptions((prev) => prev.map((o) => {
      if (o.id !== id) return o;
      const rest = { ...o };
      delete rest.score;
      return rest;
    }));

  const move = (from: number, to: number) =>
    setOptions((prev) => {
      const next = [...prev];
      const [m] = next.splice(from, 1);
      if (!m) return prev;
      next.splice(to, 0, m);
      return next;
    });

  return (
    <Modal onClose={onClose} label="Multiple choice responses">
      <ModalHeader title="Multiple choice responses" onClose={onClose}>
        <p className="text-xs text-muted-foreground">e.g. Yes, No, N/A</p>
      </ModalHeader>

      <ModalBody>
      <label className="flex items-center gap-1.5 text-sm text-foreground">
        <Checkbox value={scoring} onChange={(e) => setScoring(e.target.checked)} />
        Scoring
      </label>

      <p className="mt-4 text-label font-semibold uppercase tracking-wide text-muted-foreground">Response</p>
      <div className="mt-1 space-y-1">
        {options.map((opt, i) => (
          <div
            key={opt.id}
            className="rounded-lg border border-border/60 bg-white/40 px-2 py-2 dark:bg-white/5"
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => { if (dragIndex.current != null) move(dragIndex.current, i); dragIndex.current = null; }}
          >
            <div className="flex items-center gap-2">
              <span
                draggable
                onDragStart={() => { dragIndex.current = i; }}
                className="cursor-grab select-none text-muted-foreground"
                title="Drag to reorder"
              >
                ⠿
              </span>
              <Input
                className="flex-1"
                value={opt.label}
                onChange={(e) => patch(opt.id, { label: e.target.value })}
              />
              <ColorInput
                value={opt.color ?? '#94a3b8'}
                onChange={(e) => patch(opt.id, { color: e.target.value })}
                size="sm"
                aria-label="Response color"
                title="Response color"
              />
            </div>
            <div className="mt-1 flex items-center justify-end gap-3 text-xs text-muted-foreground">
              <label className="flex items-center gap-1">
                <Checkbox
                  value={Boolean(opt.failed)}
                  onChange={(e) => patch(opt.id, { failed: e.target.checked })}
                />
                Mark as flagged
              </label>
              {scoring && (
                <label className="flex items-center gap-1">
                  Score:
                  <Input
                    type="number"
                    className="w-16"
                    value={opt.score ?? ''}
                    placeholder="/"
                    onChange={(e) => (e.target.value === '' ? clearScore(opt.id) : patch(opt.id, { score: Number(e.target.value) }))}
                  />
                </label>
              )}
              <IconButton
                icon={<TrashIcon className="h-4 w-4" />}
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => setOptions((prev) => prev.filter((o) => o.id !== opt.id))}
                aria-label="Delete response"
              />
            </div>
          </div>
        ))}
      </div>
      <Button
        variant="ghost"
        size="fit"
        className="mt-2 text-sm font-semibold text-primary hover:bg-transparent"
        onClick={() => setOptions((prev) => [...prev, { id: newId(), label: `Option ${prev.length + 1}` }])}
      >
        + Add Response
      </Button>
      </ModalBody>

      <ModalFooter>
        <Button
          variant="primary"
          onClick={() => {
            const clean = options
              .filter((o) => o.label.trim().length > 0)
              .map((o) => {
                if (scoring) return o;
                const rest = { ...o };
                delete rest.score;
                return rest;
              });
            if (clean.length > 0) onSave(clean);
            onClose();
          }}
        >
          Save and apply
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ------------------------------------------------------------------
// Slider constraints modal — SC's "Slider" dialog: live preview,
// Min / Max / Increment, Save and apply.
// ------------------------------------------------------------------
export function SliderModal({ initial, onSave, onClose }: {
  initial: { min?: number | undefined; max?: number | undefined; step?: number | undefined };
  onSave: (v: { min: number; max: number; step: number }) => void;
  onClose: () => void;
}): JSX.Element {
  const [min, setMin] = useState(initial.min ?? 1);
  const [max, setMax] = useState(initial.max ?? 10);
  const [step, setStep] = useState(initial.step ?? 1);
  const [preview, setPreview] = useState(initial.min ?? 1);
  const valid = Number.isFinite(min) && Number.isFinite(max) && min < max && step > 0;

  return (
    <Modal onClose={onClose} label="Slider">
      <ModalHeader title="Slider" onClose={onClose}>
        <p className="text-xs text-muted-foreground">You can define the range of response with the slider.</p>
      </ModalHeader>

      <ModalBody>
      <p className="text-label font-semibold uppercase tracking-wide text-muted-foreground">Preview</p>
      <RangeInput
        className="mt-2 w-full"
        min={min}
        max={max}
        step={step}
        value={preview}
        onChange={(e) => setPreview(Number(e.target.value))}
      />
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{min}</span>
        <span className="font-bold text-foreground">{preview}</span>
        <span>{max}</span>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <label className="text-xs text-muted-foreground">
          Min
          <Input type="number" className="mt-1 w-full" value={min}
            onChange={(e) => setMin(Number(e.target.value))} />
        </label>
        <label className="text-xs text-muted-foreground">
          Max
          <Input type="number" className="mt-1 w-full" value={max}
            onChange={(e) => setMax(Number(e.target.value))} />
        </label>
        <label className="text-xs text-muted-foreground">
          Increment
          <Input type="number" className="mt-1 w-full" value={step} min={0}
            onChange={(e) => setStep(Number(e.target.value))} />
        </label>
      </div>
      {!valid && <p className="mt-2 text-xs text-destructive">Min must be below max, increment above 0.</p>}
      </ModalBody>

      <ModalFooter>
        <Button
          variant="primary"
          disabled={!valid}
          onClick={() => { onSave({ min, max, step }); onClose(); }}
        >
          Save and apply
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ------------------------------------------------------------------
// Trigger menu — SC's "+ trigger" dropdown: Ask questions, Notify (soon),
// Require note / media / action.
// ------------------------------------------------------------------
export type TriggerKind = 'askQuestions' | 'require' | 'requireNote' | 'requirePhoto' | 'requireAction';

export function TriggerMenu({ anchor, existing, onPick, onClose }: {
  anchor: HTMLElement;
  existing: string[];
  onPick: (kind: TriggerKind) => void;
  onClose: () => void;
}): JSX.Element {
  const items: Array<{ kind: TriggerKind; label: string; icon: JSX.Element; disabled?: boolean }> = [
    { kind: 'askQuestions', label: 'Ask questions', icon: <ChatBubbleLeftRightIcon className="h-4 w-4" /> },
    { kind: 'requireNote', label: 'Require note', icon: <DocumentTextIcon className="h-4 w-4" />, disabled: existing.includes('requireNote') },
    { kind: 'requirePhoto', label: 'Require media', icon: <PhotoIcon className="h-4 w-4" />, disabled: existing.includes('requirePhoto') },
    { kind: 'requireAction', label: 'Require action', icon: <ClipboardDocumentCheckIcon className="h-4 w-4" />, disabled: existing.includes('requireAction') },
    { kind: 'require', label: 'Require answer', icon: <BellAlertIcon className="h-4 w-4" />, disabled: existing.includes('require') },
  ];
  return (
    <AnchoredPanel anchor={anchor} align="left" width={224} onClose={onClose}>
      <div className="py-1">
        {items.map((item) => (
          <Button
            key={item.kind}
            variant="ghost"
            size="fit"
            disabled={item.disabled}
            className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-white/50 disabled:opacity-40 dark:hover:bg-white/10"
            onClick={() => { onPick(item.kind); onClose(); }}
          >
            <span className="flex w-full items-center gap-2">
              {item.icon}
              {item.label}
              {item.kind === 'askQuestions' && (
                <span className="ml-auto text-caption text-muted-foreground">nests below</span>
              )}
            </span>
          </Button>
        ))}
      </div>
    </AnchoredPanel>
  );
}

// ------------------------------------------------------------------
// "Add new" menu — SC's block picker: Question / Section / Page / Table.
// ------------------------------------------------------------------
export type BlockKind = 'question' | 'section' | 'page' | 'table';

export function AddNewMenu({ anchor, onPick, onClose, hideQuestion }: {
  anchor: HTMLElement;
  onPick: (kind: BlockKind) => void;
  onClose: () => void;
  hideQuestion?: boolean;
}): JSX.Element {
  const items: Array<{ kind: BlockKind; label: string; hint: string; icon: JSX.Element }> = [
    { kind: 'question', label: 'Question', hint: 'Select a response type to capture information', icon: <QuestionMarkCircleIcon className="h-5 w-5 text-primary" /> },
    { kind: 'section', label: 'Section', hint: 'Group related questions together within a section', icon: <Squares2X2Icon className="h-5 w-5 text-primary" /> },
    { kind: 'page', label: 'Page', hint: 'Organize your template with multiple pages', icon: <DocumentIcon className="h-5 w-5 text-primary" /> },
    { kind: 'table', label: 'Table', hint: 'Collect multiple responses in a structured tabular format', icon: <TableCellsIcon className="h-5 w-5 text-primary" /> },
  ];
  return (
    <AnchoredPanel anchor={anchor} align="left" width={300} onClose={onClose}>
      <div className="py-1">
        {items.filter((i) => !(hideQuestion && i.kind === 'question')).map((item) => (
          <Button
            key={item.kind}
            variant="ghost"
            size="fit"
            className="w-full px-3 py-2.5 text-left hover:bg-white/50 dark:hover:bg-white/10"
            onClick={() => { onPick(item.kind); onClose(); }}
          >
            <span className="flex w-full items-start gap-3">
              {item.icon}
              <span>
                <span className="block text-sm font-semibold text-foreground">{item.label}</span>
                <span className="block text-xs text-muted-foreground">{item.hint}</span>
              </span>
            </span>
          </Button>
        ))}
      </div>
    </AnchoredPanel>
  );
}

// ------------------------------------------------------------------
// Response-type picker popover — mirrors SafetyCulture's panel: search,
// "Multiple choice responses" (reusable, editable response sets) and
// "Other responses" (typed answers with icons).
// ------------------------------------------------------------------
export function TypePickerPopover({ anchor, onPick, onEditSet, onClose }: {
  anchor: HTMLElement;
  onPick: (patch: Partial<FormField>) => void;
  onEditSet: (options: FieldOption[]) => void;
  onClose: () => void;
}): JSX.Element {
  const [query, setQuery] = useState('');

  const otherTypes = useMemo(
    () => (Object.keys(TYPE_LABELS) as FieldType[])
      .filter((t) => t !== 'select')
      .filter((t) => TYPE_LABELS[t].toLowerCase().includes(query.toLowerCase())),
    [query],
  );
  const sets = useMemo(
    () => RESPONSE_SET_PRESETS.filter((p) => p.label.toLowerCase().includes(query.toLowerCase())),
    [query],
  );

  return (
    <AnchoredPanel anchor={anchor} align="right" width={560} onClose={onClose}>
      <div className="p-4">
        <Input
          autoFocus
          className="w-full"
          placeholder="Search response types…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="mt-3 grid grid-cols-2 gap-4">
          <div>
            <div className="flex items-center justify-between">
              <p className="text-label font-semibold uppercase tracking-wide text-muted-foreground">
                Multiple choice responses
              </p>
              <Button
                variant="ghost"
                size="fit"
                className="text-label font-semibold text-primary hover:bg-transparent"
                onClick={() => { onEditSet([]); onClose(); }}
              >
                + Responses
              </Button>
            </div>
            <div className="mt-2 space-y-1">
              {sets.map((preset) => (
                <div key={preset.label} className="group flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="fit"
                    className="flex-1 shrink rounded-lg px-2 py-1.5 text-left hover:bg-white/50 dark:hover:bg-white/10"
                    onClick={() => {
                      onPick({ type: 'select', options: preset.options.map((o) => ({ ...o, id: newId() })) });
                      onClose();
                    }}
                  >
                    <span className="flex flex-wrap items-center gap-1">
                      {preset.options.slice(0, 4).map((o) => <OptionChip key={o.label} option={o} small />)}
                      {preset.options.length > 4 && (
                        <span className="text-caption text-muted-foreground">+{preset.options.length - 4}</span>
                      )}
                    </span>
                  </Button>
                  <IconButton
                    icon={<PencilIcon className="h-3.5 w-3.5" />}
                    variant="ghost"
                    size="sm"
                    className="text-primary opacity-0 group-hover:opacity-100"
                    title="Edit responses before applying"
                    aria-label="Edit responses before applying"
                    onClick={() => {
                      onEditSet(preset.options.map((o) => ({ ...o, id: newId() })));
                      onClose();
                    }}
                  />
                </div>
              ))}
              {sets.length === 0 && <p className="px-2 text-xs text-muted-foreground">No matches</p>}
            </div>
          </div>
          <div>
            <p className="text-label font-semibold uppercase tracking-wide text-muted-foreground">Other responses</p>
            <div className="mt-2 space-y-0.5">
              {otherTypes.map((t) => (
                <Button
                  key={t}
                  variant="ghost"
                  size="fit"
                  className="w-full rounded-lg px-2 py-1.5 text-left text-sm text-foreground hover:bg-white/50 dark:hover:bg-white/10"
                  onClick={() => {
                    const patch: Partial<FormField> = { type: t };
                    if (t === 'multiselect') {
                      patch.options = [
                        { id: newId(), label: 'Option 1', color: '#0044a3' },
                        { id: newId(), label: 'Option 2', color: '#81b532' },
                      ];
                    }
                    onPick(patch);
                    onClose();
                  }}
                >
                  <span className="flex w-full items-center gap-2">
                    {TYPE_ICONS[t]}
                    {TYPE_LABELS[t]}
                  </span>
                </Button>
              ))}
              {otherTypes.length === 0 && <p className="px-2 text-xs text-muted-foreground">No matches</p>}
            </div>
          </div>
        </div>
      </div>
    </AnchoredPanel>
  );
}

// Compose a new logic rule (the inline rows render existing rules).
export function LogicComposer({ field, fieldKeys, onAddRule, onAddVisibility, onDone }: {
  field: FormField;
  fieldKeys: string[];
  onAddRule: (when: Condition) => void;
  onAddVisibility: (when: Condition) => void;
  onDone: () => void;
}): JSX.Element {
  const [when, setWhen] = useState<Condition>(() => {
    const first = field.options?.[0];
    return first
      ? { field: field.key, op: 'eq', value: first.label }
      : { field: field.key, op: 'answered' };
  });
  const [asVisibility, setAsVisibility] = useState(false);

  const add = () => {
    if (!when.field) return;
    const needsValue = !['answered', 'notAnswered'].includes(when.op);
    if (needsValue && String(when.value ?? '').trim() === '') return; // never emit "is <empty>"
    const cond: Condition = needsValue ? when : { field: when.field, op: when.op };
    if (asVisibility) onAddVisibility(cond);
    else onAddRule(cond);
    onDone();
  };

  const valueSuggestions = field.options?.map((o) => o.label) ?? [];

  return (
    <div className="space-y-2 rounded-lg border border-dashed border-amber-500/50 bg-amber-500/5 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">If</span>
        <Select aria-label="If field" value={when.field} onChange={(e) => setWhen((w) => ({ ...w, field: e.target.value }))}>
          {[field.key, ...fieldKeys.filter((k) => k !== field.key)].map((k) => (
            <option key={k} value={k}>{k === field.key ? 'answer' : k}</option>
          ))}
        </Select>
        <Select aria-label="Operator" value={when.op} onChange={(e) => setWhen((w) => ({ ...w, op: e.target.value as Condition['op'] }))}>
          {CONDITION_OPS.map((op) => <option key={op} value={op}>{opLabel(op)}</option>)}
        </Select>
        {!['answered', 'notAnswered'].includes(when.op) && (
          valueSuggestions.length > 0 && when.field === field.key ? (
            <Select
              aria-label="Value"
              value={String(when.value ?? '')}
              onChange={(e) => setWhen((w) => ({ ...w, value: e.target.value }))}
            >
              {valueSuggestions.map((v) => <option key={v} value={v}>{v}</option>)}
            </Select>
          ) : (
            <Input
              className="w-24"
              placeholder="value"
              value={String(when.value ?? '')}
              onChange={(e) => setWhen((w) => ({ ...w, value: e.target.value }))}
            />
          )
        )}
        <label className="ml-2 flex items-center gap-1 text-label text-muted-foreground">
          <Checkbox value={asVisibility} onChange={(e) => setAsVisibility(e.target.checked)} />
          visibility rule (show this question)
        </label>
      </div>
      <p className="text-label text-muted-foreground">
        The rule is added with no triggers — use <span className="font-semibold text-amber-600 dark:text-amber-400">+ trigger</span> on it to ask questions or require note / media / action.
      </p>
      <div className="flex gap-2">
        <Button variant="primary" onClick={add}>
          Add rule
        </Button>
        <Button variant="secondary" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export function RelationsEditor({ relations, fieldKeys, onChange }: {
  relations: FormRelation[];
  fieldKeys: string[];
  onChange: (relations: FormRelation[]) => void;
}): JSX.Element {
  const [type, setType] = useState<RelationType>('xor');
  const [picked, setPicked] = useState<string[]>([]);
  const [dep, setDep] = useState<{ field: string; on: string }>({ field: '', on: '' });

  const add = () => {
    if (type === 'dependsOn') {
      if (!dep.field || !dep.on || dep.field === dep.on) return;
      onChange([...relations, { type: 'dependsOn', field: dep.field, on: dep.on }]);
    } else {
      if (picked.length < 2) return;
      onChange([...relations, { type, fields: picked }]);
    }
    setPicked([]);
  };

  const describe = (r: FormRelation) =>
    r.type === 'dependsOn' ? `${r.field} dependsOn ${r.on}` : `${r.type}(${r.fields.join(', ')})`;

  return (
    <section className="rounded-xl border border-white/15 bg-white/40 p-4 dark:bg-white/5">
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        Cross-field relations — union / xor / allOf / dependsOn
      </p>
      <div className="mt-2 space-y-1">
        {relations.map((r, i) => (
          <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="rounded-full bg-primary/10 px-2 py-0.5 font-mono text-caption font-semibold text-primary">
              {describe(r)}
            </span>
            <IconButton
              icon="✕"
              variant="ghost"
              size="sm"
              aria-label="Remove relation"
              onClick={() => onChange(relations.filter((_, j) => j !== i))}
            />
          </div>
        ))}
        {relations.length === 0 && <p className="text-xs text-muted-foreground">No relations defined.</p>}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Select aria-label="Relation type" value={type} onChange={(e) => setType(e.target.value as RelationType)}>
          {(['xor', 'union', 'allOf', 'dependsOn'] as RelationType[]).map((t) => <option key={t} value={t}>{t}</option>)}
        </Select>
        {type === 'dependsOn' ? (
          <>
            <Select aria-label="Depends-on field" value={dep.field} onChange={(e) => setDep((d) => ({ ...d, field: e.target.value }))}>
              <option value="">field…</option>
              {fieldKeys.map((k) => <option key={k} value={k}>{k}</option>)}
            </Select>
            <span className="text-xs text-muted-foreground">depends on</span>
            <Select aria-label="Depends-on target field" value={dep.on} onChange={(e) => setDep((d) => ({ ...d, on: e.target.value }))}>
              <option value="">field…</option>
              {fieldKeys.map((k) => <option key={k} value={k}>{k}</option>)}
            </Select>
          </>
        ) : (
          <div className="flex flex-wrap gap-1">
            {fieldKeys.map((k) => (
              <Button
                key={k}
                variant="ghost"
                size="fit"
                pressed={picked.includes(k)}
                onClick={() => setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]))}
                className={`rounded-full border px-2 py-0.5 text-caption font-mono ${
                  picked.includes(k) ? 'border-transparent bg-primary text-white' : 'border-border text-muted-foreground'
                }`}
              >
                {k}
              </Button>
            ))}
          </div>
        )}
        <Button variant="secondary" size="sm" onClick={add}>
          Add relation
        </Button>
      </div>
    </section>
  );
}
